import { FC, useCallback, useState } from 'react'
import { AppraisalStepper } from '../../../../components/custom_components/Appraisal/AppraisalStepper/AppraisalStepper'
import ManagerMeetingStepNotes from './ManagerMeetingStepNotes'
import ManagerMeetingStepContractChanges from './ManagerMeetingStepContractChanges'
import ManagerMeetingStepEvaluation from './ManagerMeetingStepEvaluation'
import { ManagerMeetingStepPastTargets } from './ManagerMeetingStepPastTargets'
import ManagerMeetingStepNewGoals from './ManagerMeetingStepNewGoals'
import ManagerPreparationOverview from './ManagerPreparationOverview'
import ManagerPreparationSelfReflection from './ManagerPreparationSelfReflection'
import { MeetingStepViewId, MeetingStepType, MeetingStepStatus, MeetingStep, FullMeetingFragment } from '../../../../types'
import { AppraisalBackBtn, AppraisalNextBtn } from '../shared/MeetingStepButton'
import { setMeetingStep } from '../shared/AppraisalMeetingService'
import { ContentContainer } from '../../../../components/custom_components/ContentContainer'
import { ManagerMeetingStepPayments } from './ManagerMeetingStepPayments'
import { AppraisalFlowProvider } from '../shared/AppraisalFlowProvider'
import { useCollapse } from '../../../../routes/collapse-context'
import useCountrySettings from '../../../../graphql/countrySettings/hooks/useCountrySettings'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'

interface Props {
  meeting: FullMeetingFragment
}

const ManagerPrepareMeetingView: FC<Props> = ({ meeting }) => {
  const meetingId = meeting.id
  const [animate, setAnimate] = useState(false)
  const [processing, setProcessing] = useState(false)

  const collapsed = useCollapse()

  const steps: MeetingStep[] = meeting.meetingSteps
    ? [...meeting.meetingSteps.filter((step) => step.type === MeetingStepType.ManagerPrep)].sort((a, b) => a.position - b.position)
    : []

  const activeStep = steps.find((x) => meeting.managerPreparationActiveStep && x.id === meeting.managerPreparationActiveStep.id)
  const activeStepIndex = activeStep ? steps.indexOf(activeStep) : -1

  //todo: create a more robust version
  const nextStep = steps[activeStepIndex + 1]
  const previousStep = steps[activeStepIndex - 1]

  const updateMeetingStep = useCallback(
    (step: MeetingStep, currentStepStatus?: MeetingStepStatus) => {
      setAnimate(true)
      setProcessing(true)
      setMeetingStep(meetingId, step, currentStepStatus).then(() => {
        setProcessing(false)
      })
    },
    [meetingId],
  )

  const { data, error, loading } = useCountrySettings(meeting.employee.currentContract!.legalEntity.country)

  if (loading || error || !data || !data.getCountrySettings) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data || !data.getCountrySettings} error={error} />
  }

  const BackBtn = () => {
    return <AppraisalBackBtn nextStep={previousStep} onClick={() => updateMeetingStep(previousStep)} />
  }
  const NextBtn = (currentStepStatus?: MeetingStepStatus, disabled?: boolean) => {
    return <AppraisalNextBtn nextStep={nextStep} disabled={disabled} onClick={() => updateMeetingStep(nextStep, currentStepStatus)} />
  }

  return (
    <ContentContainer collapsed={collapsed}>
      <AppraisalFlowProvider meeting={meeting} steps={steps}>
        <AppraisalStepper
          meetingType={meeting.type}
          employee={meeting.employee}
          steps={steps}
          activeStepIndex={activeStepIndex}
          animate={animate}
          processing={processing}
          onStepClick={updateMeetingStep}
        />

        {activeStep && activeStep.viewId === MeetingStepViewId.Intro && <ManagerPreparationOverview meeting={meeting} nextBtn={NextBtn} />}
        {activeStep && activeStep.viewId === MeetingStepViewId.SelfReflection && (
          <ManagerPreparationSelfReflection meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
        )}
        {activeStep && activeStep.viewId === MeetingStepViewId.Evaluation && (
          <ManagerMeetingStepEvaluation meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
        )}
        {activeStep && activeStep.viewId === MeetingStepViewId.PastTargets && (
          <ManagerMeetingStepPastTargets meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
        )}
        {activeStep && activeStep.viewId === MeetingStepViewId.Payments && (
          <ManagerMeetingStepPayments meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
        )}
        {activeStep && activeStep.viewId === MeetingStepViewId.ContractChanges && (
          <ManagerMeetingStepContractChanges
            meeting={meeting}
            backBtn={BackBtn}
            nextBtn={NextBtn}
            countrySettings={data.getCountrySettings}
          />
        )}
        {activeStep && activeStep.viewId === MeetingStepViewId.NewTargets && (
          <ManagerMeetingStepNewGoals meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
        )}
        {activeStep && activeStep.viewId === MeetingStepViewId.Notes && <ManagerMeetingStepNotes meeting={meeting} backBtn={BackBtn} />}
      </AppraisalFlowProvider>
    </ContentContainer>
  )
}

export default ManagerPrepareMeetingView
